import { useEffect, useState } from 'react';
import ShopNavBar from '../../components/ShopNavBar'
import { useLocation, useNavigate } from 'react-router-dom';
import ItemListJob from '../../components/shop/ItemListJob';
import ShopQuickScan from '../../components/shop/ShopQuickScan';
import EmptyViewJobs from '../../components/shop/EmptyViewJobs';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../../contexts/AuthContext';
import { equalTo, onValue, orderByChild, query } from 'firebase/database';
import { jobRequestsDbRef } from '../../utils/dbRefs';
import { getTimeLimit } from '../../utils/utils';
import { useEnquiriesCount } from '../../hooks/useEnquiriesCount';
import { completePrintJob } from '../../utils/printJobs';


const Dashboard = () => {
    const { currentUser, userLoading } = useAuth();
    const [jobs, setJobs] = useState([]);
    const [filteredJobs, setFilteredJobs] = useState([]);
    const { unreadEnquiriesCount, loading: enquiriesLoading } = useEnquiriesCount();
    const [loading, setLoading] = useState(false);
    const [status, setStatus] = useState("all");
    const [timeRange, setTimeRange] = useState("all_time");
    const navigate = useNavigate();

    useEffect(() => {
        if (!currentUser?.uid) return;

        setLoading(true);


        const shopJobsQuery = query(
            jobRequestsDbRef,
            orderByChild("shop/uid"),
            equalTo(currentUser.uid)
        );

        const unsubscribe = onValue(shopJobsQuery, (snapshot) => {
            const data = snapshot.val();

            if (data) {
                const formatted = Object.entries(data)
                    .map(([id, value]) => ({
                        id,
                        ...value,
                    }))
                    .filter((job) => job.status === "received"); // ✅ HERE

                setJobs(formatted);
            } else {
                setJobs([]);
            }

            setLoading(false);
        });

        return () => unsubscribe();
    }, [currentUser?.uid]);

    useEffect(() => {
        let result = [...jobs];

        // 🏷 1. STATUS FILTER
        if (status !== "all") {
            if (status === "in_progress") {
                // show only received jobs (all time)
                result = result.filter((job) => job.status === "received");
            }

            if (status === "new") {
                const limit = Date.now() - 3 * 24 * 60 * 60 * 1000;

                result = result.filter((job) => {
                    return (
                        job.status === "received" &&
                        (job.createdAt || 0) >= limit
                    );
                });
            }
        }

        // ⏰ 2. TIME RANGE FILTER
        if (timeRange !== "all_time") {
            const limit = getTimeLimit(timeRange);

            result = result.filter((job) => {
                const createdAt = job.createdAt || 0;
                return createdAt >= limit;
            });
        }

        setFilteredJobs(result);
    }, [jobs, status, timeRange]);

    const handleMarkAsComplete = async (job) => {
        if (!job?.id) return;

        try {
            await completePrintJob(job.id);

            // then remove after update
            setJobs((prev) => prev.filter((j) => j.id !== job.id));

        } catch (error) {
            console.error("Error updating job status:", error);
        }
    };

    const handleViewJob = (job) => {
        navigate('/shop/job-details', { state: { job } });
    };


    return (
        <div className='bg-background min-h-full flex flex-col font-["Plus_Jakarta_Sans",sans-serif]'>
            <Helmet>
                <title>Store Dashboard - Signet Print</title>
                <meta
                    name="description"
                    content="View and manage your store's print jobs on Signet Print."
                />
            </Helmet>
            <ShopNavBar type={currentUser?.type || "shop"} />
            <div className='flex-1 max-w-[1200px] mx-auto w-full px-[16px] md:px-[40px] py-[24px] md:py-[32px] pb-[80px] md:pb-[40px]'>
                <div className="mb-[24px]">
                    <h1 className="font-semibold text-[24px] md:text-[28px] text-[#212326] mb-[4px]">
                        Welcome back, {currentUser?.shopName ? currentUser.shopName.split(" ")[0] : "Shop Owner"}!
                    </h1>
                    <p className="font-medium text-[14px] text-[#717182]">
                        Here's what's happening with your print jobs today.
                    </p>
                </div>

                <button
                    onClick={() => navigate("/supplier/enquiries")}
                    className="w-full mb-[20px] bg-[#f0f4ff] border border-[#d6e0f5] rounded-[14px] px-[20px] py-[16px] flex 
                        items-center justify-between group hover:border-[#4a6cf7] transition-colors"
                >
                    <div className="flex items-center gap-[12px]">
                        <div className="w-[40px] h-[40px] rounded-[10px] bg-[#4a6cf7] flex items-center justify-center shrink-0">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                stroke-width="2" stroke-linecap="round" stroke-linejoin="round" className="lucide lucide-mail w-[18px] h-[18px] text-white">
                                <rect width="20" height="16" x="2" y="4" rx="2"></rect>
                                <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"></path>
                            </svg>
                        </div>
                        <div className="text-left">
                            <p className="font-semibold text-[14px] text-[#212326]">{unreadEnquiriesCount || 0} new supplier enquiries</p>
                            <p className="font-medium text-[12px] text-[#717182]">Customers are looking for your supplies</p>
                        </div>
                    </div>
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                        stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                        className="lucide lucide-arrow-up-right w-[18px] h-[18px] text-[#4a6cf7] group-hover:translate-x-[2px]
                                      group-hover:-translate-y-[2px] transition-transform">
                        <path d="M7 7h10v10"></path>
                        <path d="M7 17 17 7"></path>
                    </svg>
                </button>
                <div className="flex items-center justify-between mb-[20px] flex-wrap gap-[12px]">
                    <h2 className='font-semibold text-[16px] text-[#212326]'>Latest Jobs</h2>
                    <div
                        className='flex items-center space-x-2'
                    >
                        <div className="flex items-center gap-[8px] flex-wrap">
                            <div className="relative">
                                <select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    className="appearance-none bg-white border border-[#e4e5e7] rounded-[10px] px-[14px] py-[9px] pr-[34px] text-[13px] font-medium text-[#212326] cursor-pointer
                                 hover:border-[#c0c2c6] focus:outline-none focus:border-[#ff5323] transition-colors"
                                >
                                    <option value="all">All Status</option>
                                    <option value="new">New</option>
                                    <option value="in_progress">In Progress</option>
                                </select>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                    stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                    class="lucide lucide-chevron-down absolute right-[10px] top-1/2 -translate-y-1/2 w-[14px] h-[14px] text-[#9298a0] pointer-events-none">
                                    <path d="m6 9 6 6 6-6"></path>
                                </svg>
                            </div>
                            <div className="relative">
                                <select
                                    value={timeRange}
                                    onChange={(e) => setTimeRange(e.target.value)}
                                    className="appearance-none bg-white border border-[#e4e5e7] rounded-[10px] px-[14px] py-[9px] pr-[34px] text-[13px] font-medium text-[#212326] 
                                            cursor-pointer hover:border-[#c0c2c6] focus:outline-none focus:border-[#ff5323] transition-colors">
                                    <option value="today">Today</option>
                                    <option value="this_week">This week</option>
                                    <option value="this_month">This month</option>
                                    <option value="all_time">All time</option>
                                </select>
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                                    stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                                    class="lucide lucide-chevron-down absolute right-[10px] top-1/2 -translate-y-1/2 w-[14px] h-[14px] text-[#9298a0] pointer-events-none">
                                    <path d="m6 9 6 6 6-6"></path>
                                </svg>
                            </div>
                        </div>

                    </div>
                </div>


                {loading ? (
                    // Display loading spinner/message while fetching jobs
                    <div className="fixed inset-0 flex items-center justify-center bg-gray-500 bg-opacity-50 z-50">
                        <div className="bg-white p-4 rounded-md shadow-lg">
                            <p className="text-lg font-semibold">Loading...</p>
                        </div>
                    </div>
                ) :
                    filteredJobs.length > 0 ? (
                        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[12px]'>
                            {
                                filteredJobs.map((job, index) => (
                                    <ItemListJob
                                        job={job}
                                        index={index}
                                        handleViewJob={handleViewJob}
                                        handleMarkAsComplete={handleMarkAsComplete}
                                    />
                                ))}
                        </div>
                    ) : (
                        <EmptyViewJobs
                            title="No active jobs"
                            description="Jobs will appear here once they're marked as active."
                        />
                    )}
                {
                    !userLoading && (
                        <ShopQuickScan user={currentUser || {}} />
                    )
                }

            </div>
        </div>

    )
}

export default Dashboard;
